import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Star } from "lucide-react";
import { api } from "../lib/api";
import { stripEmoji } from "../lib/text";

// Rotating widget of 4-5 star Goodreads reviews for the book.
export default function ReviewsRotator() {
  const [reviews, setReviews] = useState([]);
  const [i, setI] = useState(0);

  useEffect(() => {
    api.get("/reviews").then((r) => setReviews(r.data.reviews || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (reviews.length < 2) return undefined;
    const t = setInterval(() => setI((p) => (p + 1) % reviews.length), 6500);
    return () => clearInterval(t);
  }, [reviews.length]);

  if (!reviews.length) return null;
  const idx = i % reviews.length;
  const r = reviews[idx];

  return (
    <div data-testid="reviews-widget" className="mt-6 border border-onaccent/25 bg-ink/85 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex gap-0.5" aria-label={`${r.rating} out of 5 stars`}>
          {Array.from({ length: 5 }).map((_, s) => (
            <Star key={s} className={`w-3.5 h-3.5 ${s < r.rating ? "fill-current text-onaccent" : "text-onaccent/30"}`} />
          ))}
        </div>
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-onaccent/60">Reader reviews · Goodreads</span>
      </div>
      <AnimatePresence mode="wait">
        <motion.div
          key={idx}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        >
          <p data-testid="review-text" className="text-sm leading-relaxed text-onaccent/90 italic line-clamp-5">“{stripEmoji(r.text)}”</p>
          <p className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-onaccent/70">— {stripEmoji(r.name)}</p>
        </motion.div>
      </AnimatePresence>
      {reviews.length > 1 && (
        <div className="mt-5 flex gap-1.5">
          {reviews.map((rev, d) => (
            <button
              key={`${rev.name}-${d}`}
              onClick={() => setI(d)}
              data-testid={`review-dot-${d}`}
              aria-label={`Show review ${d + 1}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${d === idx ? "w-5 bg-onaccent" : "w-1.5 bg-onaccent/40 hover:bg-onaccent/70"}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
